import { useCallback, useEffect, useMemo, useState } from 'react';
import { canLoadBookingNotifications } from '../auth/userLoginType';
import { fetchBookingsList } from '../api/bookingApi';
import { normalizeBookingListRecords } from '../utils/bookingListNormalize';
import {
  filterActiveMyBookingsListRecords,
  filterBookingsStartingWithinWindow,
  UPCOMING_BOOKING_NOTIFICATION_WINDOW_MS,
} from '../utils/bookingRecordMutate';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export function useUpcomingBookingNotifications(
  enabled: boolean,
  loginType: string | null | undefined,
) {
  const bookingsEnabled = enabled && canLoadBookingNotifications(loginType);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [allBookings, setAllBookings] = useState<Record<string, unknown>[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    if (!bookingsEnabled) {
      setAllBookings([]);
      setLoadState('idle');
      setLoadError(null);
      return;
    }
    setLoadState('loading');
    setLoadError(null);
    try {
      const data = await fetchBookingsList();
      const records = normalizeBookingListRecords(data);
      setAllBookings(filterActiveMyBookingsListRecords(records));
      setNowMs(Date.now());
      setLoadState('ready');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setLoadError(message);
      setAllBookings([]);
      setLoadState('error');
    }
  }, [bookingsEnabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!bookingsEnabled) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, [bookingsEnabled]);

  useEffect(() => {
    if (!bookingsEnabled) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [bookingsEnabled, refresh]);

  const upcoming = useMemo(
    () =>
      filterBookingsStartingWithinWindow(
        allBookings,
        UPCOMING_BOOKING_NOTIFICATION_WINDOW_MS,
        nowMs,
      ),
    [allBookings, nowMs],
  );

  return {
    upcoming,
    hasUpcoming: upcoming.length > 0,
    loadState,
    loadError,
    refresh,
  };
}
